import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageWithSupportTicketId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { encodeSupportTicketFilterGroup } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { and, eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import type {
  PresenceCollection,
  SupportTicketPresenceRecord,
} from "../realtime/support-ticket-presence-collection";
import type { SupportTicketListCollectionWithLoader } from "./support-ticket-list-collection";
import { SUPPORT_TICKET_PREFETCH_PAGES_AHEAD } from "./support-ticket-list-requests";
import {
  supportTicketDetailQueryKey,
  supportTicketsPageQueryKey,
} from "./support-ticket-query-keys";
import { filterGroupToTanstackExpression } from "./support-ticket-tanstack-expressions";
import type {
  MessagesCollection,
  SupportTicketThreadCollectionItem,
  ThreadsCollection,
  TimelineEventsCollection,
} from "./support-ticket-transcript-collections";

const STALE_TIME = 5 * 60 * 1000;

interface PageMetadata {
  supportTicketIds: string[];
  totalCount: number;
  page: number;
}

export function orderSupportTicketsByPageIds(
  supportTickets: readonly SupportTicketListItemResponse[],
  supportTicketIds: readonly string[],
): SupportTicketListItemResponse[] {
  const orderById = new Map(
    supportTicketIds.map((supportTicketId, index) => [supportTicketId, index]),
  );

  return [...supportTickets].sort((a, b) => {
    const aOrder = orderById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = orderById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });
}

const supportTicketListCountFields = [
  "numDrafts",
  "numSentMessages",
  "numOutboundMessages",
  "numCustomerMessages",
  "numMerchantSentMessages",
  "numAiSentMessages",
  "numAutoReplySentMessages",
  "numSystemSentMessages",
  "numInternalNotes",
] as const satisfies readonly (keyof SupportTicketListItemResponse)[];

export function mergeSupportTicketDetailForCollection(
  detail: SupportTicketListItemResponse,
  existing: SupportTicketListItemResponse | undefined,
): SupportTicketListItemResponse {
  if (!existing) {
    return detail;
  }

  const merged = { ...detail };
  for (const field of supportTicketListCountFields) {
    merged[field] = existing[field];
  }
  return merged;
}

export interface SupportTicketDataCollections {
  supportTicketsList: SupportTicketListCollectionWithLoader;
  threads: ThreadsCollection;
  messages: MessagesCollection;
  presence: PresenceCollection;
  timelineEvents: TimelineEventsCollection;
}

export function useSupportTicketsData({
  page,
  collections,
  filterGroup,
  pageSize,
}: {
  page: number;
  collections: SupportTicketDataCollections;
  filterGroup: FilterGroup;
  pageSize?: number;
}): {
  data: SupportTicketListItemResponse[];
  totalCount: number;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();
  const encodedFilterGroup = filterGroup
    ? encodeSupportTicketFilterGroup(filterGroup)
    : undefined;

  const queryScope = collections.supportTicketsList.queryScope;

  const { data: pageMetadata, isLoading: isFetching } = useQuery({
    queryKey: [
      ...supportTicketsPageQueryKey(page, encodedFilterGroup, queryScope),
      pageSize,
    ],
    queryFn: async (): Promise<PageMetadata> => {
      const result = await collections.supportTicketsList.fetchPage(
        page,
        encodedFilterGroup,
        pageSize,
      );
      return {
        supportTicketIds: result.items.map((item) => item.id),
        totalCount: result.totalCount,
        page: result.page,
      };
    },
    staleTime: STALE_TIME,
  });

  const supportTicketIds = useMemo(
    () => pageMetadata?.supportTicketIds ?? [],
    [pageMetadata?.supportTicketIds],
  );
  const idsKey = supportTicketIds.join(",");

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ supportTicket: collections.supportTicketsList.collection })
        .where(({ supportTicket }) => {
          const idFilter = inArray(supportTicket.id, supportTicketIds);
          const filterExpr = filterGroup
            ? filterGroupToTanstackExpression(filterGroup, supportTicket)
            : null;
          return filterExpr ? and(idFilter, filterExpr) : idFilter;
        }),
    [idsKey, encodedFilterGroup, collections.supportTicketsList.collection],
  );
  const orderedData = useMemo(
    () => orderSupportTicketsByPageIds(liveResult.data, supportTicketIds),
    [liveResult.data, supportTicketIds],
  );

  useEffect(() => {
    for (let i = 1; i <= SUPPORT_TICKET_PREFETCH_PAGES_AHEAD; i++) {
      const pageToLoad = page + i;
      void queryClient.prefetchQuery({
        queryKey: [
          ...supportTicketsPageQueryKey(
            pageToLoad,
            encodedFilterGroup,
            queryScope,
          ),
          pageSize,
        ],
        queryFn: async (): Promise<PageMetadata> => {
          const result = await collections.supportTicketsList.fetchPage(
            pageToLoad,
            encodedFilterGroup,
            pageSize,
          );
          return {
            supportTicketIds: result.items.map((item) => item.id),
            totalCount: result.totalCount,
            page: result.page,
          };
        },
        staleTime: STALE_TIME,
      });
    }
  }, [
    page,
    pageSize,
    encodedFilterGroup,
    queryClient,
    collections.supportTicketsList,
    queryScope,
  ]);

  return {
    data: orderedData,
    totalCount: pageMetadata?.totalCount ?? 0,
    isLoading: isFetching || liveResult.isLoading,
  };
}

export function useSupportTicketData({
  supportTicketId,
  collections,
  fetchSupportTicket,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
  fetchSupportTicket: (
    supportTicketId: SupportTicketId,
  ) => Promise<SupportTicketListItemResponse>;
}): { data: SupportTicketListItemResponse | undefined; isLoading: boolean } {
  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ supportTicket: collections.supportTicketsList.collection })
        .where(({ supportTicket }) => eq(supportTicket.id, supportTicketId))
        .findOne(),
    [supportTicketId, collections.supportTicketsList.collection],
  );

  const { isLoading: isFetchingSingle } = useQuery({
    queryKey: supportTicketDetailQueryKey(
      supportTicketId,
      collections.supportTicketsList.queryScope,
    ),
    queryFn: async () => {
      const supportTicket = await fetchSupportTicket(supportTicketId);
      const existing =
        collections.supportTicketsList.collection.get(supportTicketId);
      collections.supportTicketsList.collection.utils.writeUpsert(
        mergeSupportTicketDetailForCollection(supportTicket, existing),
      );
      return supportTicket;
    },
    enabled: liveResult.data === undefined,
    staleTime: STALE_TIME,
  });

  return {
    data: liveResult.data,
    isLoading: liveResult.isLoading || isFetchingSingle,
  };
}

export function useChannelThreadsData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: SupportTicketThreadCollectionItem[]; isLoading: boolean } {
  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ thread: collections.threads })
        .where(({ thread }) => eq(thread.supportTicketId, supportTicketId))
        .orderBy(({ thread }) => thread.createdAt, "asc"),
    [supportTicketId],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useSupportTicketMessagesData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: MessageWithSupportTicketId[]; isLoading: boolean } {
  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.supportTicketId, supportTicketId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [supportTicketId],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useThreadMessagesData({
  supportTicketId,
  threadIds,
  collections,
}: {
  supportTicketId: SupportTicketId;
  threadIds: ChannelThreadId | readonly ChannelThreadId[];
  collections: SupportTicketDataCollections;
}): { data: Message[]; isLoading: boolean } {
  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.supportTicketId, supportTicketId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [supportTicketId],
  );

  const threadIdSet = useMemo(
    () => new Set(Array.isArray(threadIds) ? threadIds : [threadIds]),
    [threadIds],
  );

  const data = useMemo(
    () => result.data.filter((m) => threadIdSet.has(m.channelThreadId)),
    [result.data, threadIdSet],
  );

  return { data, isLoading: result.isLoading };
}

export function useSupportTicketTimelineEventsData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: SupportTicketTimelineEventResponse[]; isLoading: boolean } {
  const result = useLiveQuery(
    (q) =>
      q
        .from({ event: collections.timelineEvents })
        .where(({ event }) => eq(event.supportTicketId, supportTicketId))
        .orderBy(({ event }) => event.occurredAt, "desc"),
    [supportTicketId],
  );

  return { data: result.data ?? [], isLoading: result.isLoading };
}

export function useSupportTicketPresenceData({
  supportTicketId,
  collections,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
}): { data: SupportTicketPresenceRecord[] } {
  const result = useLiveQuery(
    (q) =>
      q
        .from({ presence: collections.presence })
        .where(({ presence }) => eq(presence.supportTicketId, supportTicketId)),
    [supportTicketId],
  );

  return { data: result.data };
}

export function useSingleSupportTicketDataCore({
  supportTicketId,
  collections,
  fetchSupportTicket,
}: {
  supportTicketId: SupportTicketId;
  collections: SupportTicketDataCollections;
  fetchSupportTicket: (
    supportTicketId: SupportTicketId,
  ) => Promise<SupportTicketListItemResponse>;
}): {
  supportTicket: SupportTicketListItemResponse | undefined;
  threads: SupportTicketThreadCollectionItem[];
  messages: Message[];
  isLoading: boolean;
} {
  const supportTicketResult = useSupportTicketData({
    supportTicketId,
    collections,
    fetchSupportTicket,
  });
  const threadsResult = useChannelThreadsData({ supportTicketId, collections });
  const messagesResult = useSupportTicketMessagesData({
    supportTicketId,
    collections,
  });

  return {
    supportTicket: supportTicketResult.data,
    threads: threadsResult.data,
    messages: messagesResult.data,
    isLoading:
      threadsResult.isLoading ||
      messagesResult.isLoading ||
      supportTicketResult.isLoading,
  };
}

export { DEFAULT_SUPPORT_TICKET_PAGE_SIZE } from "./support-ticket-list-requests";
